import { DOCUMENT, NgClass, NgTemplateOutlet } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { RouterLink } from '@angular/router';
import { HamburgerMenuBottomSheetComponent } from '@components/bottom-sheets/hamburger-menu-bottom-sheet/hamburger-menu-bottom-sheet.component';
import { ButtonComponent } from '@components/buttons/button/button.component';
import { IconButtonComponent } from '@components/buttons/icon-button/icon-button.component';
import { TagButtonComponent } from '@components/buttons/tag-button/tag-button.component';
import {
  ConfirmDialogComponent,
  IConfirmData,
} from '@components/dialogs/confirm-dialog/confirm-dialog.component';
import { ConfirmParentalControlDialogComponent } from '@components/dialogs/confirm-parental-control-dialog/confirm-parental-control-dialog.component';
import { ImageGeneratorComponent } from '@components/image-generator/image-generator.component';
import { staticImages } from '@shared/helper/configs';
import { IAccessTokenProfile } from '@shared/models/access-token.interface';
import { GeneralService } from '@shared/services/general.service';
import { TokenService } from '@shared/services/token.service';
import { UserService } from '@shared/services/user.service';
import { Subscription, fromEvent } from 'rxjs';

@Component({
  selector: 'app-header',
  imports: [
    MatIconModule,
    MatDividerModule,
    RouterLink,
    MatButtonModule,
    MatMenuModule,
    MatSnackBarModule,
    ButtonComponent,
    IconButtonComponent,
    ImageGeneratorComponent,
    TagButtonComponent,
    NgClass,
    NgTemplateOutlet,
  ],
  providers: [UserService],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
  staticImages = staticImages;
  scrollEvent$!: Subscription;

  public tokenService = inject(TokenService);
  public generalService = inject(GeneralService);
  private readonly userService = inject(UserService);
  private readonly dialog = inject(MatDialog);
  private readonly matBottom = inject(MatBottomSheet);
  private readonly document = inject(DOCUMENT);

  ngOnInit() {
    this.handleScrollPosition();
  }

  private handleScrollPosition() {
    if (this.generalService.isBrowserOnly) {
      let prevScrollPos = window.scrollY;
      this.scrollEvent$ = fromEvent(window, 'scroll').subscribe({
        next: () => {
          const header = this.document.getElementById('main-header');
          if (header) {
            let currentScrollPos = window.scrollY;
            if (currentScrollPos === 0) {
              header.classList.remove('show');
              header.style.top = '0';
              return;
            }
            /* if scrolling down, let it scroll out of view as normal */
            if (prevScrollPos <= currentScrollPos && currentScrollPos > 400) {
              header.classList.remove('show');
              const width = window.innerWidth;
              if (width > 768) {
                header.style.top = 'calc(-1 * var(--header-height))';
              } else {
                header.style.top = 'calc(-1 * var(--header-height-mobile))';
              }
            } else {
              /* otherwise if we're scrolling up, fix the nav to the top */
              header.classList.add('show');
              header.style.top = '0';
            }
            prevScrollPos = currentScrollPos;
          }
        },
      });
    }
  }

  selectProfile(item: IAccessTokenProfile) {
    this.userService.selectProfile(item.id).subscribe({
      next: (res) => {
        if (res.statusCode === HttpStatusCode.Ok) {
          if (this.generalService.isBrowserOnly) {
            this.tokenService.setUserInfo(res.data?.atk!, res.data?.rtk!);
            this.generalService.router.navigate(['/preparation'], {
              queryParams: {
                ru: window.location.pathname,
              },
            });
          }
        }
      },
      error: ({ networkError }) => {
        if (
          networkError.statusCode === HttpStatusCode.Forbidden ||
          networkError.statusCode === HttpStatusCode.TooManyRequests
        ) {
          const confirmDialog = this.dialog.open(ConfirmParentalControlDialogComponent, {
            data: {
              profileId: item.id,
            },
          });
          confirmDialog.afterClosed().subscribe({
            next: (data) => {
              if (data) {
                this.generalService.router.navigate(['/preparation'], {
                  queryParams: {
                    ru: window.location.pathname,
                  },
                });
              }
            },
          });
        }
      },
    });
  }

  logout() {
    const confirmData: IConfirmData = {
      title: 'خروج از حساب کاربری',
      message: `آیا از خروج از حساب کاربری خود اطمینان دارید ؟`,
    };
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: confirmData,
    });
    confirmDialog.afterClosed().subscribe({
      next: (res) => {
        if (res) {
          if (this.generalService.isBrowserOnly) {
            this.tokenService.logout(window.location.pathname).subscribe();
          }
        }
      },
    });
  }

  navigateMenuItem(url: string) {
    this.generalService.router.navigateByUrl(url);
  }

  handleNavigatePlans() {
    if (this.tokenService.userInfo()) {
      this.generalService.router.navigateByUrl('/account/plans');
    } else {
      this.generalService.router.navigate(['/auth'], {
        queryParams: {
          ru: '/account/plans',
        },
      });
    }
  }

  showHamburgerMenu() {
    this.matBottom.open(HamburgerMenuBottomSheetComponent, {
      panelClass: 'hamburger-menu-bottom-sheet',
    });
  }
}
